import { RelatedPerson } from "design-system/components/pages/article-page/article-related-people/article-related-people";
import { ArticleTeaseProps } from "design-system/components/primitives/article-tease/article-tease";
import { ArticleTeaseRisExtensionProps } from "design-system/components/primitives/article-tease/article-tease-ris-extension";
import { BylineAuthor } from "design-system/components/primitives/byline/byline";
import { MediaAssetImageProps } from "design-system/components/primitives/media-asset/media-asset-image";
import {
  FacultyProfileHit,
  HonorsAwardsHit,
  LegacySearchHit,
  ModernSearchHit,
  PageProps,
  PersonTease,
  PublicationHit,
  SearchDisplay,
  SearchHit,
} from "frontend/types";
import { formatPersonName } from "design-system/utils/text";
import { stripHtml } from "../components/utils/strip-html";
import { isSearchHitType } from "./is-search-hit-type";
import { cleanSearchHightlight } from "./search-highlight";

// RIS publication contentTypes shown as a user-friendly overline (e.g.
// "Published Article" -> "Article").
const RIS_CONTENT_TYPE_LABELS: Record<string, string> = {
  Book: "Book",
  "Book Component": "Book Chapter",
  "Course Materials": "Course Materials",
  "Featured Work": "Featured Work",
  "HBS Case Material": "Case",
  "HBS Teaching and Training Material": "Teaching Material",
  "Honors and Awards": "Awards & Honors",
  "Interactive Media": "Interactive Media",
  "Non-HBS Case Material": "Case",
  "Non-HBS Teaching and Training Material": "Teaching Material",
  Other: "Other Publication",
  Presentation: "Presentation",
  "Published Article": "Article",
  "Teaching Materials": "Teaching Materials",
  "Unpublished Work": "Other Publication",
  "Working Paper": "Working Paper",
};

const risContentTypeLabel = (contentType: string): string =>
  RIS_CONTENT_TYPE_LABELS[contentType] ?? contentType;

// Source/overline labels (drop the redundant "HBS " where the brand is implied).
const SOURCE_LABELS: Record<string, string> = {
  "HBS Working Knowledge": "Working Knowledge",
};

const sourceLabel = (source: string): string => SOURCE_LABELS[source] ?? source;

const UNDATED_PUBLICATION_CATEGORIES = new Set([
  "Research Summaries",
  "Teaching Interests",
]);

const STATUS_FALLBACK_CONTENT_TYPES = new Set([
  "Book",
  "Book Component",
  "Published Article",
  "Unpublished Work",
]);

export function formatRisContributorName(name: string): string {
  return formatPersonName(name);
}

// The complete author list lives in the citation. RISContributorNames only
// contains contributors tracked by HBS.
export function parseRisCitationAuthors(
  citation: string | undefined,
  title: string | undefined,
): Array<string> {
  if (!citation || !title) return [];
  const plain = stripHtml(citation);
  const idx = plain.indexOf(title);
  if (idx <= 0) return [];
  const segment = plain
    .slice(0, idx)
    .replace(/[\s"“”]+$/u, "")
    .replace(/\.\s+Comment on$/i, "")
    .replace(/([a-z])\.$/u, "$1")
    .trim();
  if (!segment) return [];

  const parts = segment
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 0) return [];
  if (parts.length === 1) return [parts[0]!.replace(/^and\s+/i, "")];

  const authors = [`${parts[0]}, ${parts[1]}`];
  for (let i = 2; i < parts.length; i++) {
    authors.push(parts[i]!.replace(/^and\s+/i, "").trim());
  }
  return authors.filter(Boolean);
}

export function createSearchArticleTease(
  data?: SearchHit,
  siteId?: string,
  options?: {
    thumbnailProps?: Partial<MediaAssetImageProps>;
    pageProps?: PageProps;
    featureFacultyInArticles?: boolean;
    portraitLimit?: number;
  },
) {
  if (
    isSearchHitType<ModernSearchHit>("modernArticle", data) ||
    isSearchHitType<PublicationHit>("risPublication", data) ||
    isSearchHitType<HonorsAwardsHit>("risHonorsAwards", data)
  ) {
    return createModernSearchArticleTease(data, siteId, {
      pageProps: options?.pageProps,
      featureFacultyInArticles: options?.featureFacultyInArticles || false,
      portraitLimit: options?.portraitLimit,
    });
  }
  if (isSearchHitType<LegacySearchHit>("legacyArticle", data)) {
    return createLegacySearchArticleTease(data, options?.thumbnailProps);
  }

  // If you hit this, please verify on the functions above if the hit has the required properties
  console.error(
    "Search hit does not comply to ModernSearchHit or LegacySearchHit constraints",
  );
}

export function createLegacySearchArticleTease(
  data: LegacySearchHit,
  thumbnailProps?: Partial<MediaAssetImageProps>,
): ArticleTeaseProps {
  const props: ArticleTeaseProps = {
    type: "Article",
    title: data.HBSTitle,
    link: data.HBSLink,
    tease: data.HBSDescription,
    byline: {
      publicationDate: data.HBSPubDate,
      type: "ShowAll",
    },
    sortDate: data.HBSSortDate,
  };

  if (data.HBSSource) {
    props.overline = { label: sourceLabel(data.HBSSource) };
  }

  if (data.HBSByline) {
    let people = data.HBSByline;
    let authors = data.HBSByline;

    if (authors.startsWith("by ") || authors.startsWith("By ")) {
      authors = authors.substring(3);
    }

    if (authors.startsWith("by: ") || authors.startsWith("By: ")) {
      authors = authors.substring(4);
    }

    if (people.startsWith("re ") || people.startsWith("Re ")) {
      people = people.substring(3);
    }

    if (people.startsWith("re: ") || people.startsWith("Re: ")) {
      people = people.substring(4);

      if (people.indexOf(", ") > 0) {
        let peopleCleanup = "";

        people.split(", ").forEach((person) => {
          if (person.indexOf("@") > 0) return; // skip email addresses
          peopleCleanup = peopleCleanup
            ? peopleCleanup + ", " + person
            : person;
        });

        people = peopleCleanup;
      }
    }

    if (authors !== data.HBSByline) props.byline!.authors = [{ name: authors }];
    if (people !== data.HBSByline) {
      props.byline!.featuredAuthors = [{ name: people }];
    }
  }

  if (data.HBSThumbnail) {
    props.image = {
      src: data.HBSThumbnail,
      ...thumbnailProps,
    };
  }

  return props;
}

export function createModernSearchArticleTease(
  data: ModernSearchHit | SearchDisplay,
  siteId?: string,
  options?: {
    pageProps?: PageProps;
    featureFacultyInArticles?: boolean;
    portraitLimit?: number;
  },
): ArticleTeaseProps {
  let description = data.description;

  //Modern Search Hit with "legacy" subset only used on sitewide Search Pages
  if (data.subset?.includes("legacy") && !description) {
    description = data.body;
  }

  if (
    data.highlight?.description &&
    Array.isArray(data.highlight?.description)
  ) {
    description = data.highlight?.description[0];
  }

  if (data.highlight?.body && Array.isArray(data.highlight?.body)) {
    description = cleanSearchHightlight(data.highlight?.body[0]);
  }

  // Use the body as a last resort if no description is set
  if (!description && data.body) {
    description = data.body.substring(0, 500).trim();
  }

  // Use the description (e.g. the RIS abstract). No description → no tease.
  const tease = description && stripHtml(description);

  const props: ArticleTeaseProps = {
    type: "Article",
    title: data.title,
    link: resolveSearchResultLink(data),
    tease: tease,
  };

  //if data type = ModernSearchHit then use data.display
  //if data type = SearchDisplay then use data
  //if data type = LegacySearchHit then use data.display
  if (isSearchHitType<SearchDisplay>("modern", data)) {
    if (data.display?.loginRequired) {
      const userIsLoggedIn = options?.pageProps?.session?.user.loggedIn;
      console.info("userIsLoggedIn", userIsLoggedIn);
      if (!userIsLoggedIn) {
        props.title = `${props.title} <em class="hbs-article-tease__teaser login-required">(login required)</em>`;
      }
    }
    // Display the category as an overline when entry is for the site
    if (siteId && data.display?.site === siteId) {
      if (data.display?.overline) {
        props.overline = data.display.overline;
      } else if (data.display?.category) {
        props.overline = data.display.category;
      }

      // Display the Site name as an overline when entry is from a different site
    } else if (data.display?.siteOverline) {
      props.overline = data.display.siteOverline;
    } else if (data.display?.category) {
      props.overline = data.display.category;
    }

    if (
      !props.overline &&
      data.contentType &&
      RIS_CONTENT_TYPE_LABELS[data.contentType]
    ) {
      props.overline = {
        label:
          data.subset?.includes("ris-products") && data.display?.subType
            ? data.display.subType
            : risContentTypeLabel(data.contentType),
      };
    }
    props.id = data.id;
    if (data.display?.thumbnail?.src) {
      props.image = data.display.thumbnail;
      if (data.display.thumbnail.gravity) {
        props.image.gravity = data.display.thumbnail.gravity;
      }
      if (data.display.thumbnail.aspectRatio) {
        props.image.aspectRatio = data.display.thumbnail.aspectRatio;
      }
    }

    if (isRisFacultyProfileHit(data) && props.image) {
      props.image.aspectRatio = "1/1";
    }

    if (data.display?.date) {
      props.byline = {
        publicationDate: data.display.date,
      };
    }

    if (data.display?.byline) {
      if (
        data.display.byline[0]?.label &&
        (data.display.byline[0].label.toLowerCase().startsWith("by") ||
          data.display.byline[0].label.toLowerCase().startsWith("re"))
      ) {
        // temporary add for supporting legacy byline data (legacy loader now in modern ES setup)
        const legacyByline = stripHtml(data.display.byline[0].label);

        let people = legacyByline;
        let authors = legacyByline;

        if (authors.startsWith("by ") || authors.startsWith("By ")) {
          authors = authors.substring(3);
        }

        if (authors.startsWith("by: ") || authors.startsWith("By: ")) {
          authors = authors.substring(4);
        }

        if (people.startsWith("re ") || people.startsWith("Re ")) {
          people = people.substring(3);
        }

        if (people.startsWith("re: ") || people.startsWith("Re: ")) {
          people = people.substring(4);

          if (people.indexOf(", ") > 0) {
            let peopleCleanup = "";

            people.split(", ").forEach((person) => {
              if (person.indexOf("@") > 0) return; // skip email addresses
              peopleCleanup = peopleCleanup
                ? peopleCleanup + ", " + person
                : person;
            });

            people = peopleCleanup;
          }
        }

        if (props.byline) {
          if (authors !== legacyByline) {
            props.byline.authors = [{ name: authors }];
            props.byline.type = "ShowAll";
          }
          if (people !== legacyByline) {
            props.byline.featuredAuthors = [{ name: people }];
            props.byline.type = "ShowAll";
          }
        } else {
          if (authors !== legacyByline) {
            props.byline = { authors: [{ name: authors }], type: "ShowAll" };
          }
          if (people !== legacyByline) {
            props.byline = {
              featuredAuthors: [{ name: people }],
              type: "ShowAll",
            };
          }
        }
      } else {
        const byline: Array<BylineAuthor> = [];
        const hightlight = data.highlight;

        data.display.byline.forEach((author) => {
          if (author.label) {
            let label = author.label;
            if (
              hightlight?.["author.text"] &&
              Array.isArray(hightlight?.["author.text"]) &&
              hightlight?.["author.text"][0]
            ) {
              const authorFirstHighlight = hightlight?.["author.text"][0]
                ? hightlight?.["author.text"][0]
                    .replaceAll("<mark>", "")
                    .replaceAll("</mark>", "")
                : "";
              if (
                hightlight?.["author.text"][0] &&
                authorFirstHighlight === label
              ) {
                label = hightlight?.["author.text"][0];
              }
            }
            byline.push({ name: label, link: author.link });
          }
        });

        if (data.subset?.includes("artwork")) {
          props.byline = { authors: byline, type: "ShowAll" };
        } else if (props.byline) {
          props.byline.authors = byline;
        } else {
          props.byline = { authors: byline };
        }

        if (options?.featureFacultyInArticles && data.display?.facultyTeases) {
          const facultyPersonSet = new Set<string>();
          const queryFeaturedFaculty: Array<RelatedPerson> = [];
          let totalFacultyCount = 0;
          data.display.facultyTeases.forEach((personTease: PersonTease) => {
            if (!facultyPersonSet.has(personTease.name)) {
              facultyPersonSet.add(personTease.name);

              const featuredFacultyProps: RelatedPerson = {
                name: personTease.name,
                titles: personTease.titles ?? [],
                link: personTease.url,
              };
              if (
                personTease.thumbnail?.src &&
                ((options?.portraitLimit &&
                  totalFacultyCount < options?.portraitLimit) ||
                  !options?.portraitLimit)
              ) {
                featuredFacultyProps.portrait = {
                  image: personTease.thumbnail,
                };
                totalFacultyCount++;
              }

              // Only show current hbs faculty: non-faculty do not have links
              if (personTease.url) {
                queryFeaturedFaculty.push(featuredFacultyProps);
              }
            }
          });
          if (props.byline) {
            const authors = props.byline.authors || [];
            queryFeaturedFaculty.forEach((faculty) => {
              props.byline!.featuredAuthors =
                props.byline!.featuredAuthors || [];
              const avatarImage = faculty.portrait?.image;
              props.byline!.featuredAuthors.push({
                name: faculty.name,
                link: faculty.link,
                ...(avatarImage ? { avatar: { image: avatarImage } } : {}),
              });
            });
            if (
              props.byline.featuredAuthors &&
              props.byline.featuredAuthors.length > 0
            ) {
              props.byline.headshots = true;
              props.byline.type = "ShowAll";
            }
            props.byline.authors = authors;
          }
        }

        if (data.subset?.includes("associationReference")) {
          const bylineWithAffiliation = byline.map((author, i) => {
            const aff = Array.isArray(data.affiliation)
              ? data.affiliation[i]
              : data.affiliation;
            const affText = Array.isArray(aff) ? aff.join(", ") : (aff ?? "");
            if (!affText) return author;
            return {
              ...author,
              name: `${author.name}, ${affText}`,
            };
          });

          props.byline = {
            authors: bylineWithAffiliation,
            type: "AssociationReference",
          };
        }
      }

      if (data.display?.featuredFaculty?.length) {
        if (!props.byline) props.byline = {};
        props.byline.featuredAuthors = data.display.featuredFaculty.map(
          (f) => ({
            name: f.label || "",
            link: f.link,
          }),
        );
        props.byline.type = "ShowAll";
      }

      if (data.display.downloadLink) {
        props.downloadLink = data.display.downloadLink;
      }
      if (data.display.caseHead) {
        props.display = {
          caseHead: data.display.caseHead,
          caseCategory: data.display.caseCategory,
        };
      }
    }
  } else {
    // TODO: Add category url support to modern search schema
    if (data.source) {
      props.overline = {
        label: sourceLabel(data.source) /*, link: data.sourceUrl */,
      };
    } else if (data.contentType) {
      props.overline = {
        label: risContentTypeLabel(
          data.contentType,
        ) /*, link: data.contentTypeUrl */,
      };
    }
  }

  if (data.subset?.includes("artwork")) {
    props.variant = "artwork";
  }

  if (
    data.subset?.includes("participantStory") ||
    data.subset?.includes("profilePages") ||
    data.subset?.includes("studentLeadership")
  ) {
    props.variant = "profile";
  }

  if (
    data.source?.includes("HBS Magazine") ||
    data.source?.includes("Alumni Bulletin")
  ) {
    props.variant = "magazine";
    if (props.byline) props.byline.type = "ShowAllPeople";
    if (
      isSearchHitType<SearchDisplay>("modern", data) &&
      data.display?.overline?.label
    ) {
      props.overline = { label: data.display.overline.label };
    }
  }

  if (data.subset?.includes("studentStory")) {
    props.variant = "student-story";
    if (props.image) {
      props.image.aspectRatio = "1/1";
    }
    props.byline = {};
  }

  if (isSearchHitType<PublicationHit>("risPublication", data)) {
    const citationAuthors = parseRisCitationAuthors(
      data.display?.citation,
      data.title,
    ).map(formatRisContributorName);
    const authors = citationAuthors.length
      ? citationAuthors
      : (data.display?.contributorNames ?? data.author ?? []).map(
          formatRisContributorName,
        );
    const suppressPublicationDate = data.category?.some((category) =>
      UNDATED_PUBLICATION_CATEGORIES.has(category),
    );
    const publicationDate = suppressPublicationDate
      ? undefined
      : data.display?.publicationDate;
    let publicationDateText: string | undefined;
    if (
      !suppressPublicationDate &&
      !publicationDate &&
      data.contentType &&
      STATUS_FALLBACK_CONTENT_TYPES.has(data.contentType)
    ) {
      publicationDateText = data.display?.publicationStatus;
    }
    const publicationCalendarDate = publicationDate
      ? (publicationDate instanceof Date
          ? publicationDate.toISOString()
          : String(publicationDate)
        ).split("T")[0]
      : undefined;

    if (authors.length || publicationDate || publicationDateText) {
      props.byline = {
        ...props.byline,
        authors: authors.length
          ? authors.map((name) => ({
              name,
            }))
          : undefined,
        publicationDate: publicationCalendarDate,
        publicationDateText,
        publicationDateFormat:
          data.display?.publicationYear && !data.display.publicationMonth
            ? "year"
            : "monthYear",
        type: "ShowAll",
      };
    }

    props.risExtension = buildRisExtension(data);
    // Publication teases never show an image.
    props.image = undefined;
    // Use the clean abstract for the blurb, not the search-highlight fragment
    // (which starts mid-sentence around the matched query term).
    props.tease = data.description ? stripHtml(data.description) : undefined;
  }

  if (isSearchHitType<HonorsAwardsHit>("risHonorsAwards", data)) {
    const personId = data.personId?.[0]?.replace(/^ris-faculty-/, "");
    const recipients = (data.author ?? []).map(formatRisContributorName);

    props.overline = {
      label: risContentTypeLabel("Honors and Awards"),
    };
    props.link = personId
      ? `/faculty/profile/${personId}#AwardsHonors`
      : undefined;
    props.image = undefined;
    if (recipients.length) {
      props.byline = {
        authors: recipients.map((name) => ({ name })),
        authorLabel: "Awarded to",
        type: "ShowAll",
      };
    }
    props.tease = data.description
      ? stripHtml(data.description)
      : data.display?.citation
        ? stripHtml(data.display.citation)
        : undefined;
  }

  return props;
}

function resolveSearchResultLink(
  data: ModernSearchHit | SearchDisplay,
): string {
  if (isRisFacultyProfileHit(data)) {
    return normalizeFacultyProfileLink(data.url);
  }

  if (data.subset?.includes("ris-products")) {
    return normalizeFacultyPublicationLink(data.url);
  }

  return data.url;
}

export function isRisFacultyProfileHit(
  data: ModernSearchHit | SearchDisplay,
): data is FacultyProfileHit {
  return data.subset?.includes("risFaculty") ?? false;
}

export function normalizeFacultyProfileLink(link: string): string {
  try {
    const url = new URL(link);

    if (
      url.hostname === "www.hbs.edu" &&
      url.pathname.startsWith("/faculty/profile/")
    ) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
  } catch {
    // Relative or malformed URLs should keep their original value.
  }

  return link;
}

function normalizeFacultyPublicationLink(link: string): string {
  try {
    const url = new URL(link);
    const productId = url.searchParams.get("num");

    if (
      url.hostname === "www.hbs.edu" &&
      url.pathname.toLowerCase() === "/faculty/pages/item.aspx" &&
      productId
    ) {
      return `/faculty/publications/${productId}${url.hash}`;
    }
  } catch {
    // Relative or malformed URLs should keep their original value.
  }

  return link;
}

function buildRisKeywords(
  data: PublicationHit,
): Array<{ label: string; link: string }> {
  const fields: Array<[string, Array<string> | undefined]> = [
    ["keyword", data.display?.risKeywords],
    ["topic", data.display?.risTopics],
    ["industry", data.display?.risIndustries],
    ["geography", data.display?.risGeographies],
  ];

  return fields.flatMap(([key, values]) => {
    const param = key === "keyword" ? "q" : key;
    return (values || []).map((value) => ({
      label: value,
      link: `/faculty/publications?${param}=${encodeURIComponent(value)}`,
    }));
  });
}

function buildRisExtension(
  data: PublicationHit,
): ArticleTeaseRisExtensionProps {
  const display = data.display;

  const keywords = buildRisKeywords(data);

  const ctas = (display?.ctas ?? [])
    .filter((c) => c.label && c.link)
    .map((c, i) => ({
      label: c.label as string,
      href: c.link as string,
      type: (i === 0 ? "primary-button" : "secondary-button") as
        | "primary-button"
        | "secondary-button",
    }));

  const personId = data.personId?.[0]?.replace(/^ris-faculty-/, "");

  return {
    keywords: keywords.length > 0 ? keywords : undefined,
    citation: display?.citation,
    ctas: ctas.length > 0 ? ctas : undefined,
    moreFromAuthorEndpoint: personId
      ? `/api/faculty/recent?personId=${encodeURIComponent(
          personId,
        )}&excludeId=${encodeURIComponent(data.id)}`
      : undefined,
  };
}
